"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  RefreshCw,
  Copy,
  Check,
  Compass,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAudioFeedback } from "@/hooks/useAudioFeedback";

const SAMPLE_NAMES = [
  { name: "Aurelius Vane", realm: "High Elf Noble", meaning: "Golden beacon of dawn", slug: "elven-names" },
  { name: "Ignis Voragath", realm: "Elder Dragon", meaning: "Cataclysmic mountain cinder", slug: "dragon-names" },
  { name: "Sylas Nightwhisper", realm: "Shadow Rogue", meaning: "Silent phantom of the crypts", slug: "assassin-names" },
  { name: "Valeria Stormstride", realm: "Valkyrie Paladin", meaning: "Shieldmaiden of thunderous skies", slug: "valkyrie-names" },
  { name: "Zephyr Thorne", realm: "Cyberpunk Netrunner", meaning: "Ghost operative in neon alleys", slug: "cyberpunk-names" },
];

export default function Hero() {
  const [currentSampleIndex, setCurrentSampleIndex] = useState(0);
  const [isRolling, setIsRolling] = useState(false);
  const [copied, setCopied] = useState(false);
  const { playPop, playChime } = useAudioFeedback();

  const sample = SAMPLE_NAMES[currentSampleIndex];

  const handleRollSample = () => {
    setIsRolling(true);
    playPop();
    setTimeout(() => {
      setCurrentSampleIndex((prev) => (prev + 1) % SAMPLE_NAMES.length);
      setIsRolling(false);
      playChime();
    }, 220);
  };

  const handleCopySample = () => {
    navigator.clipboard.writeText(sample.name);
    setCopied(true);
    playChime();
    toast.success(`Copied "${sample.name}" to clipboard! ✨`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="relative overflow-hidden pt-28 pb-16 sm:pt-36 sm:pb-24 px-4">
      {/* Multi-Layered Arcane & Fantasy Background */}
      <div className="absolute inset-0 -z-20 overflow-hidden pointer-events-none select-none">
        {/* Atmospheric Mythic Landscape / Starfield Base */}
        <Image
          src="/images/hero/arcane-celestial.jpg"
          alt="Arcane Celestial Atmosphere"
          fill
          priority
          sizes="100vw"
          className="object-cover opacity-25 dark:opacity-35 scale-105"
        />

        {/* Soft Vignette & Parchment/Dark Theme Blend Mask */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/70 to-background" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-500/15 via-transparent to-transparent dark:from-amber-400/10" />

        {/* Concentric Celestial Arcane Geometry Rings */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[550px] sm:w-[680px] h-[550px] sm:h-[680px] rounded-full border border-primary/20 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[750px] sm:w-[920px] h-[750px] sm:h-[920px] rounded-full border border-dashed border-primary/15 pointer-events-none animate-[spin_60s_linear_infinite]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[950px] sm:w-[1180px] h-[950px] sm:h-[1180px] rounded-full border border-dotted border-amber-500/10 pointer-events-none" />

        {/* Soft Ambient Radial Light Flares */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] sm:w-[700px] h-[350px] bg-primary/20 dark:bg-primary/25 rounded-full blur-[100px]" />
        <div className="absolute top-1/3 -left-20 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-[90px]" />
        <div className="absolute top-1/3 -right-20 w-[400px] h-[400px] bg-yellow-500/10 rounded-full blur-[90px]" />
      </div>

      <div className="max-w-5xl mx-auto text-center relative z-10">
        {/* Curated Lore Pill */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-6 shadow-sm backdrop-blur-md">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Every Name Has A Spell In It &bull; Name Arcane</span>
        </div>

        {/* Main Display Headline (Immediate Render for Fast LCP) */}
        <h1 className="text-4xl sm:text-6xl md:text-7xl font-fantasy font-extrabold tracking-tight text-foreground mb-6 leading-[1.12]">
          The Infinite <span className="text-primary">Name Generator</span>
        </h1>

        {/* Editorial Subtitle */}
        <p className="text-base sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
          Forge unique fantasy characters, mythical beasts, sci-fi galaxies, historical cultures, and tabletop RPG names with deep origins and meanings.
        </p>

        {/* Interactive Live Forge Preview Card */}
        <div className="max-w-xl mx-auto mb-10 p-4 sm:p-5 rounded-3xl border border-border/80 bg-card/90 backdrop-blur-xl shadow-xl">
          <div className="flex items-center justify-between gap-3 text-left">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[11px] font-semibold text-primary uppercase tracking-wider">
                  Live Lore Forge &bull; {sample.realm}
                </span>
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={sample.name}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                >
                  <p className="text-xl sm:text-2xl font-bold font-fantasy text-foreground truncate">
                    {sample.name}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                    &ldquo;{sample.meaning}&rdquo;
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopySample}
                className="h-10 w-10 rounded-2xl border-border/80 hover:border-primary/50"
                title="Copy Name"
                aria-label="Copy generated name"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
              </Button>
              <Button
                variant="fantasy"
                size="icon"
                onClick={handleRollSample}
                disabled={isRolling}
                className="h-10 w-10 rounded-2xl shadow-md"
                title="Roll New Name"
                aria-label="Roll new sample name"
              >
                <RefreshCw className={`w-4 h-4 ${isRolling ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </div>

        {/* Primary CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-wrap items-center justify-center gap-3.5 mb-12"
        >
          <Link href="#categories">
            <Button
              variant="fantasy"
              size="lg"
              className="rounded-2xl px-6 h-12 shadow-lg shadow-primary/20 text-sm sm:text-base"
            >
              <span>Explore Realms Now</span>
            </Button>
          </Link>

          <Link href="/directory">
            <Button
              variant="outline"
              size="lg"
              className="rounded-2xl px-6 h-12 border-border/80 hover:border-primary/50 text-sm sm:text-base"
            >
              <Compass className="w-4 h-4 mr-2" />
              <span>Browse All Generators</span>
            </Button>
          </Link>
        </motion.div>

        {/* Fast Topic Jump Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="flex items-center justify-center gap-2 flex-wrap text-xs text-muted-foreground"
        >
          <span className="font-semibold text-foreground">Popular Generators:</span>
          {[
            { label: "✨ Elf Names", href: "/fantasy/elf-names" },
            { label: "🐉 Dragon Names", href: "/fantasy/dragon-names" },
            { label: "🧙‍♂️ Wizard Names", href: "/fantasy/wizard-names" },
            { label: "🏰 Kingdom Names", href: "/places/kingdom-names" },
            { label: "⚔️ Viking Names", href: "/real/viking-names" },
          ].map((topic) => (
            <Link
              key={topic.label}
              href={topic.href}
              className="px-2.5 py-1 rounded-lg border border-border/60 bg-card/60 hover:bg-primary/10 hover:text-primary hover:border-primary/40 transition-all font-medium"
            >
              {topic.label}
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
